/**
 * Agency Dashboard (super-admin only).
 *
 * The agency-level cockpit GHL has and we lacked: see every client tenant at
 * once, aggregate counts, drill into a tenant's config, jump into a client
 * (tenant switch), and provision a new client by cloning a reference tenant's
 * config. Wired to the cross-tenant /api/admin/super/* + snapshot endpoints
 * (api/admin.py, api/snapshot.py) that already existed headless.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, ExternalLink, Settings, Plus, RefreshCw, ShieldAlert, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  listAllTenants,
  listTenantConfigs,
  listAllUsers,
  getTenantDetail,
  provisionFromReference,
  type AgencyTenant,
  type AgencyTenantConfig,
  type AgencyUser,
  type TenantDetail,
} from '@/lib/api/agency';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Row extends AgencyTenant {
  modules: Record<string, boolean>;
}

function moduleBadges(modules: Record<string, boolean>) {
  const on = Object.entries(modules || {}).filter(([, v]) => v).map(([k]) => k);
  if (on.length === 0) return <span className="text-xs text-muted-foreground">none</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {on.map((m) => (
        <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>
      ))}
    </div>
  );
}

export function AgencyDashboard() {
  const { user, switchTenant } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows] = useState<Row[]>([]);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // config drawer
  const [detailTenant, setDetailTenant] = useState<string | null>(null);
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // provision dialog
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [refTenant, setRefTenant] = useState('');
  const [newTenantId, setNewTenantId] = useState('');
  const [provisioning, setProvisioning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenants, configs, users] = await Promise.all([
        listAllTenants(),
        listTenantConfigs().catch(() => [] as AgencyTenantConfig[]),
        listAllUsers().catch(() => [] as AgencyUser[]),
      ]);
      const cfgByTenant = new Map(configs.map((c) => [c.tenant_id, c.modules || {}]));
      const merged: Row[] = tenants.map((t) => ({ ...t, modules: cfgByTenant.get(t.tenant_id) || {} }));
      merged.sort((a, b) => a.name.localeCompare(b.name));
      setRows(merged);
      setUserCount(users.length || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConfig = async (tenantId: string) => {
    setDetailTenant(tenantId);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await getTenantDetail(tenantId));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tenant config');
    } finally {
      setDetailLoading(false);
    }
  };

  const openClient = async (tenantId: string) => {
    try {
      await switchTenant(tenantId); // scopes this browser session to that tenant, then reloads into it
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to switch tenant');
    }
  };

  const doProvision = async () => {
    if (!refTenant || !newTenantId.trim()) {
      toast.error('Pick a reference tenant and enter a new tenant id');
      return;
    }
    setProvisioning(true);
    try {
      await provisionFromReference(refTenant, newTenantId.trim());
      toast.success(`Provisioned "${newTenantId.trim()}" from ${refTenant}`);
      setProvisionOpen(false);
      setNewTenantId('');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Provision failed');
    } finally {
      setProvisioning(false);
    }
  };

  // Super-admin gate (mirrors VoiceAgentConfig).
  if (user && !user.is_super_admin) {
    return (
      <div className="p-8">
        <Card className="border-red-500/50 bg-red-500/10 max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" /> Super-admin access required
            </CardTitle>
            <CardDescription>The agency dashboard is restricted to super-admins.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow>Command Center</Eyebrow>
          <h1 className="text-2xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-6 w-6" /> Agency
          </h1>
          <p className="text-sm text-muted-foreground">All client tenants across the platform.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setProvisionOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> Provision client
          </Button>
        </div>
      </div>

      {/* Aggregate cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Clients</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold flex items-center gap-2">
              <Building2 className="h-6 w-6 text-muted-foreground" />
              {loading ? <Skeleton className="h-8 w-12" /> : rows.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Total users</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-muted-foreground" />
              {loading ? <Skeleton className="h-8 w-12" /> : (userCount ?? rows.reduce((s, r) => s + (r.user_count || 0), 0))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Dialer-enabled</CardDescription></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {loading ? <Skeleton className="h-8 w-12" /> : rows.filter((r) => r.modules?.sales_dialer).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients table */}
      <Card>
        <CardHeader>
          <CardTitle>Clients</CardTitle>
          <CardDescription>One row per tenant. Open switches you into that client; Config shows its setup.</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">No tenants found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Tenant ID</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Modules</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.tenant_id}>
                    <TableCell className="font-medium">{r.name || r.tenant_id}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{r.tenant_id}</TableCell>
                    <TableCell>{r.user_count ?? 0}</TableCell>
                    <TableCell>{moduleBadges(r.modules)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/agency/setup/${r.tenant_id}`)}>
                          <Wrench className="h-4 w-4 mr-1" /> Setup
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openConfig(r.tenant_id)}>
                          <Settings className="h-4 w-4 mr-1" /> Config
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openClient(r.tenant_id)}>
                          <ExternalLink className="h-4 w-4 mr-1" /> Open
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Config drawer */}
      <Dialog open={!!detailTenant} onOpenChange={(o) => { if (!o) { setDetailTenant(null); setDetail(null); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Config - {detailTenant}</DialogTitle>
            <DialogDescription>Read-only view of this tenant's configuration (secrets masked server-side).</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
          ) : detail ? (
            <div className="space-y-4">
              {detail.modules && (
                <div>
                  <Label className="text-xs text-muted-foreground">Modules</Label>
                  <div className="mt-1">{moduleBadges(detail.modules)}</div>
                </div>
              )}
              {detail.integrations && (
                <div>
                  <Label className="text-xs text-muted-foreground">Integrations</Label>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.keys(detail.integrations).length === 0
                      ? <span className="text-xs text-muted-foreground">none configured</span>
                      : Object.keys(detail.integrations).map((k) => <Badge key={k} variant="outline">{k}</Badge>)}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Raw config</Label>
                <pre className="mt-1 text-xs bg-muted rounded-md p-3 overflow-x-auto max-h-64">
                  {JSON.stringify(detail, null, 2)}
                </pre>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No config.</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Provision dialog */}
      <Dialog open={provisionOpen} onOpenChange={setProvisionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Provision a new client</DialogTitle>
            <DialogDescription>
              Clones a reference tenant's <strong>config</strong> (pipelines, fields, views, modules) into a new
              tenant id. It copies setup, not live data. Protected tenants (dvw4ea, buildgravity) are rejected
              server-side.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Reference tenant (copy config from)</Label>
              <select
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={refTenant}
                onChange={(e) => setRefTenant(e.target.value)}
              >
                <option value="">Select a reference tenant…</option>
                {rows.map((r) => <option key={r.tenant_id} value={r.tenant_id}>{r.name || r.tenant_id}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-tenant-id">New tenant id</Label>
              <Input
                id="new-tenant-id"
                placeholder="e.g. acme-co"
                value={newTenantId}
                onChange={(e) => setNewTenantId(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProvisionOpen(false)} disabled={provisioning}>Cancel</Button>
            <Button onClick={doProvision} disabled={provisioning}>
              {provisioning ? 'Provisioning…' : 'Provision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
